import { curCode } from "./currencies";

export const CURRENCY_FIELD_HELP = "The currency shown is selected by default — if this was in a different currency, pick the right one here - most currencies convert to US dollars automatically, and if we have no published rate for the one you pick you will be asked to enter it. Add the currencies you use in Settings and they\u2019ll appear at the top of this list, so the ones you reach for most are a tap away; everything else is alphabetical below.";

export const STATE_NAMES = {AL:"Alabama",AK:"Alaska",AZ:"Arizona",AR:"Arkansas",CA:"California",CO:"Colorado",CT:"Connecticut",DE:"Delaware",FL:"Florida",GA:"Georgia",HI:"Hawaii",ID:"Idaho",IL:"Illinois",IN:"Indiana",IA:"Iowa",KS:"Kansas",KY:"Kentucky",LA:"Louisiana",ME:"Maine",MD:"Maryland",MA:"Massachusetts",MI:"Michigan",MN:"Minnesota",MS:"Mississippi",MO:"Missouri",MT:"Montana",NE:"Nebraska",NV:"Nevada",NH:"New Hampshire",NJ:"New Jersey",NM:"New Mexico",NY:"New York",NC:"North Carolina",ND:"North Dakota",OH:"Ohio",OK:"Oklahoma",OR:"Oregon",PA:"Pennsylvania",RI:"Rhode Island",SC:"South Carolina",SD:"South Dakota",TN:"Tennessee",TX:"Texas",UT:"Utah",VT:"Vermont",VA:"Virginia",WA:"Washington",WV:"West Virginia",WI:"Wisconsin",WY:"Wyoming",DC:"District of Columbia"};
export const stateName = (code) => STATE_NAMES[code] || code;


// How every screen writes an amount. (ZEN-776) Owner ruling, 15 Oct 2026:
// US dollars as $1,234.50; every other currency by its code, CAD 1,234.50,
// never a dollar sign. Server-made PDFs follow the same rule (backend/money.py).
// `accounting` puts a negative in brackets, as the summary tables do.
export const money = (n, cur = "USD", { accounting = false } = {}) => {
  if (n == null || isNaN(n)) return "—";
  const code = curCode(cur, "USD");
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency", currency: code,
      currencyDisplay: code === "USD" ? "symbol" : "code",
      // Two decimals for every currency, as the PDFs write it: the app stores
      // amounts to the cent, so yen shows JPY 250.00, not JPY 250.
      minimumFractionDigits: 2, maximumFractionDigits: 2,
      ...(accounting ? { currencySign: "accounting" } : {}),
    }).format(n);
  } catch {
    return `${code} ${Number(n).toFixed(2)}`;
  }
};

// THE OLD FLAT `CATEGORIES` LIST IS GONE. (ZEN-100)
//
// It held ["Tuition & Fees", "Rent / Room & Board", "Food", "Books",
// "Supplies/Equipment", "Other"] and by the end it had exactly one job left:
// being the `|| CATEGORIES` fallback behind GROUP_CATEGORIES[group] on the
// 529-ELIGIBLE pickers. That made it the worst possible fallback — any screen
// that could not resolve an education group silently offered a legacy list
// containing "Other", a category that does not exist under Section 529, on the
// one side of the app where the list is the IRS reference and not the family's
// to edit. The per-call `.filter(c => c !== "Other — enter manually")`
// workarounds did not catch it either, because the string in this list was the
// bare "Other".
//
// The fallback now lives in ONE place — categoryGate.irsCategoriesFor — and it
// resolves to a real education group's real list. See DEFAULT_EXPENSE_GROUP.

// ---- 529 Expense Groups & Categories ----
export const EXPENSE_GROUPS = [
  { key: "post_secondary", label: "Higher Education" },
  { key: "k12", label: "K-12 Education" },
  { key: "apprenticeship", label: "Apprenticeship (DOL-Registered)" },
  { key: "credentialing", label: "Postsecondary Credentialing" },
];

export const GROUP_CATEGORIES = {
  post_secondary: [
    "Tuition & Fees", "Books", "Supplies/Equipment", "Computers/Technology",
    "Room & Board", "Housing", "Food", "Special Needs Services",
  ],
  k12: [
    "Tuition", "Curriculum", "Books", "Online Educational Materials", "Tutoring", "Disability Therapy",
    "Dual Enrollment Fees", "Standardized Test Fees",
  ],
  apprenticeship: [
    "Fees", "Books", "Supplies/Equipment",
  ],
  credentialing: [
    "Tuition & Fees", "Books", "Supplies/Equipment", "Required Testing Fees",
    "Continuing-Education Fees",
  ],
};

// The education group a row falls back to when it names none — a beneficiary
// with no active school, or a legacy row saved before the field existed.
// MATCHES THE SERVER on purpose: backend/expense_group_categories.py declares
// the same default, and an absent group has always been read as post-secondary
// on both sides. Naming it here means the eligible pickers fall back to a REAL
// group's real list instead of the retired flat list that carried "Other".
export const DEFAULT_EXPENSE_GROUP = "post_secondary";

// AOTC-eligible categories (Higher Education / post_secondary ONLY).
// Source: IRS Pub 970 — AOTC covers tuition, required enrollment fees, and course materials.
export const AOTC_ELIGIBLE_CATEGORIES = new Set([
  "Tuition & Fees", "Books", "Supplies/Equipment", "Computers/Technology",
]);
// Categories requiring extra confirmation for AOTC
export const AOTC_COMPUTER_GATE_CATEGORIES = new Set(["Computers/Technology"]);

// Cap amounts are prose copies of docs/IRS-529-rules-spec.md — update there first if a statutory limit changes.
export const CATEGORY_TOOLTIPS = {
  // Post-Secondary
  "Room & Board": "On- or off-campus housing and food. Qualifies only if the beneficiary is enrolled at least half-time, and expenses do not exceed the school's official Cost of Attendance (COA) allowance.",
  "Housing": "On- or off-campus housing and food. Qualifies only if the beneficiary is enrolled at least half-time, and expenses do not exceed the school's official Cost of Attendance (COA) allowance.",
  "Food": "On- or off-campus housing and food. Qualifies only if the beneficiary is enrolled at least half-time, and expenses do not exceed the school's official Cost of Attendance (COA) allowance.",
  "Computers/Technology": "Computers, peripheral equipment (e.g., printers), software, and internet access, if used primarily by the beneficiary while enrolled.",
  "Special Needs Services": "Special-needs expenses incurred in connection with enrollment.",
  // K-12 (group-specific — use key prefix)
  "k12:Tuition": "Up to $20,000 per year per beneficiary can be used for tuition at any public, private, or religious elementary or secondary school.",
  "k12:Curriculum": "Curricular and instructional materials — textbooks, workbooks, online educational materials, and other materials used for the beneficiary's education.",
  "k12:Tutoring": "Tutoring expenses qualify only if the tutoring is provided by a person who is not related to the beneficiary and who is licensed or otherwise qualified — for example, a state-licensed teacher, a person who has taught at an eligible educational institution, or a subject-matter expert in the subject being tutored.",
  "k12:Disability Therapy": "Educational therapies for beneficiaries with disabilities.",
  "k12:Dual Enrollment Fees": "Fees for postsecondary (college-level) courses taken while the beneficiary is still enrolled in K-12.",
  "k12:Online Educational Materials": "Online educational materials (e-books, digital curricula, and similar electronic resources) used for the beneficiary's K-12 education (OBBBA).",
  // Apprenticeship
  "apprenticeship:Fees": "Fees, books, supplies, and equipment required for a Department of Labor–registered apprenticeship program (no tuition).",
  "apprenticeship:Books": "Fees, books, supplies, and equipment required for a Department of Labor–registered apprenticeship program (no tuition).",
  "apprenticeship:Supplies/Equipment": "Fees, books, supplies, and equipment required for a Department of Labor–registered apprenticeship program (no tuition).",
  // Credentialing
  "credentialing:Tuition & Fees": "Tuition, fees, books, supplies, equipment, required testing, and continuing-education fees for a recognized postsecondary credential program (OBBBA).",
  "credentialing:Books": "Tuition, fees, books, supplies, equipment, required testing, and continuing-education fees for a recognized postsecondary credential program (OBBBA).",
  "credentialing:Supplies/Equipment": "Tuition, fees, books, supplies, equipment, required testing, and continuing-education fees for a recognized postsecondary credential program (OBBBA).",
  "credentialing:Required Testing Fees": "Tuition, fees, books, supplies, equipment, required testing, and continuing-education fees for a recognized postsecondary credential program (OBBBA).",
  "credentialing:Continuing-Education Fees": "Tuition, fees, books, supplies, equipment, required testing, and continuing-education fees for a recognized postsecondary credential program (OBBBA).",
  // Additional (Student Loan Repayment and Roth IRA Rollover are now 529 distributions, not expense categories)
};

export const CUSTOM_CATEGORY_MAX_LENGTH = 22; // length of "Standardized Test Fees"

export const GROUP_DISCLAIMER_FOOTER = "Rules shown reflect 2026 federal law (including the One Big Beautiful Bill Act). Some states have not conformed, so certain expenses may be qualified federally but not for state tax purposes. Confirm with your state's 529 plan.";

export const GROUP_EXPANSION_BANNER = "2026 expansion: These categories were newly added or increased under federal law. Some states have not conformed and may treat these withdrawals as non-qualified for state tax.";

// Category colors — intentionally distinct from STATUS colors (red/orange/green/gray)
// to avoid visual confusion. Uses blues, violet, teal, pink, brown, slate.
export const CATEGORY_COLOR = {
  // Post-Secondary
  "Tuition & Fees": "#034AA2",
  Tuition: "#034AA2",
  Fees: "#0E7490",
  "Rent / Room & Board": "#0E7490",
  "Room & Board": "#0E7490",
  Housing: "#0E7490",
  Food: "#DB2777",
  Books: "#92400E",
  "Supplies & Equipment": "#4338CA",
  "Supplies/Equipment": "#4338CA",
  "Computers/Technology": "#6D28D9",
  "Special Needs Services": "#0E7490",
  // K-12
  Curriculum: "#B45309",
  Tutoring: "#0369A1",
  "Disability Therapy": "#334155",
  "Dual Enrollment Fees": "#155E75",
  "Standardized Test Fees": "#7C2D12",
  // Additional (Roth/Loan now under Withdrawals, not expenses)
  Other: "#1E293B",
};

// stable fallback palette for any custom/unknown categories (no status hues)
const CATEGORY_FALLBACK = ["#034AA2", "#0E7490", "#D97706", "#DB2777", "#92400E", "#4338CA", "#1E293B", "#0369A1", "#155E75", "#059669"];
export const categoryColor = (name) => {
  if (CATEGORY_COLOR[name]) return CATEGORY_COLOR[name];
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return CATEGORY_FALLBACK[h % CATEGORY_FALLBACK.length];
};

// status -> {label, bg, fg, icon name (phosphor), short}
export const STATUS_META = {
  draft: { label: "To classify", short: "To classify", bg: "#9CA3AF", fg: "#fff", icon: "PencilSimpleLine" },
  // Recorded but not yet paid — next month's rent. Neutral blue, not amber:
  // nothing is wrong and nothing is owed from the owner, it simply hasn't
  // happened yet. It settles into a real status on its date. (ZEN-109)
  scheduled: { label: "Scheduled", short: "Scheduled", bg: "#034AA2", fg: "#fff", icon: "CalendarBlank" },
  confirmed: { label: "Awaiting", short: "Awaiting", bg: "#DC2626", fg: "#fff", icon: "WarningCircle" },
  in_progress: { label: "In progress", short: "In progress", bg: "#D97706", fg: "#fff", icon: "ClockCountdown" },
  reconciled: { label: "Reconciled", short: "Reconciled", bg: "#22C55E", fg: "#fff", icon: "CheckCircle" },
  closed: { label: "Closed", short: "Closed", bg: "#1C1917", fg: "#fff", icon: "SealCheck" },
  // ★ THE ONE ON THIS PAGE THAT WAS ALREADY REACHING FAMILIES. (ZEN-635.)
  //
  // Splitting an expense retires the original and records it as `split`. Every
  // other screen hides retired originals; MONEY TRACE DOES NOT — it lists every
  // expense for a beneficiary with no filter at all (routes/dashboard.py, the
  // /ledger door) and draws this tag for each one. So the tag had nowhere to
  // look `split` up and printed it, in grey, to anybody who had ever divided an
  // expense into parts.
  //
  // Money Trace goes on showing the original beside its parts — Maryse's
  // decision, 2026-09-15: it helps a reader follow what happened. It just says
  // so in words now.
  split: { label: "Split into parts", short: "Split", bg: "#7C3AED", fg: "#fff", icon: "ArrowsSplit" },
};

// ── WHAT A RECEIPT'S STORED WORD MEANS TO A SCREEN ──────────────────────────
//
// THE SCREENS' LIST, and the reason it exists is the defect it prevents. The
// Imports screen used to sort receipts with one question — "is it pending?" —
// and file every other answer under "Processed" with a green tick beside it.
// That is not a rule, it is a coincidence that held while there were exactly
// three words. ZEN-634 adds a fourth, "set aside", which means the opposite of
// finished; under the old question it would have been shown to a family as
// done, and nothing would have failed.
//
// THREE, AND THE TICKET SAID FOUR. ZEN-634 listed a receipt as "pending,
// attached, confirmed or rejected". `confirmed` is an EXPENSE status — it is in
// STATUS_META a few lines above — and no door has ever written it onto a
// receipt. The real set is derived from the code and declared in
// models.RECEIPT_STATUSES on the server; backend/tests/test_receipt_status_
// parity.py fails if this map and that declaration disagree in either
// direction.
//
// ★ THE HOLE THAT LETS THAT HAPPEN IS ZEN-336, STILL OPEN, AND IT IS NOT FIXED
// HERE. Nothing checks that a word the server can store is a word the screens
// can name, so the guard against it is human: a status added on the server gets
// added HERE in the same commit. That is what ZEN-634 did, and it is said out
// loud rather than assumed.
//
// UNTIL THEN THE FALLBACK IS THE SAFE WAY TO BE WRONG. A word this list does
// not know lands in the queue — still work, still visible, still reviewable —
// and never in the finished pile. Being told to look at something twice costs
// a minute; being told a receipt is dealt with when it is not is how paperwork
// goes missing.
export const RECEIPT_STATUS_BUCKET = {
  pending: "queue",        // nobody has opened it yet
  set_aside: "set_aside",  // somebody read it and is not ready to decide
  attached: "done",        // it proves an expense
  rejected: "done",        // somebody decided not to make an expense from it
};

// THE THREE PILES THE IMPORTS SCREEN ACTUALLY RENDERS. Named here because the
// lookup above was trusting whatever it found: a mistyped pile on a future
// status — "dnoe" for "done" — came back unchanged, matched no pile on the
// screen, and the receipt appeared in NONE of them. Silently vanishing is worse
// than the wrong pile, and worse than the raw-word fallback this rule replaced.
// A value that is not one of these three is treated exactly like a word the map
// does not know: it goes to the queue, where somebody will look at it again.
export const RECEIPT_PILES = ["queue", "set_aside", "done"];

export const receiptBucket = (status) => {
  const pile = RECEIPT_STATUS_BUCKET[status];
  return RECEIPT_PILES.includes(pile) ? pile : "queue";
};

// ══════════════════════════════════════════════════════════════════════════
// THE WORDS A FAMILY READS, DECLARED WHERE ONE TEST CAN CHECK THEM  (ZEN-635)
// ══════════════════════════════════════════════════════════════════════════
//
// THE FAULT THIS ENDS. Four screens turned a stored word into something a
// person reads by looking it up in a small table kept on the page itself, and
// when the word was not in the table they printed the word itself. A
// beneficiary could read `awaiting_owner_approval` where "With owner" belongs.
// Nothing failed, nothing went red, the screen kept working — which is exactly
// why nobody noticed. Found by sweeping for the shape while building ZEN-634,
// not by anybody hitting it.
//
// TWO THINGS HAD TO BE TRUE AT ONCE for that to reach a family, and both are
// now closed:
//   (1) the screen's table is a HAND COPY of a set the server owns, so it can
//       fall behind — `cancelled_by_student` really was missing below, written
//       by the cancel door and named nowhere on the beneficiary's screen; and
//   (2) when it does fall behind, the screen prints the raw word instead of
//       saying something a person can read.
//
// (1) is answered by declaring each set once on the server
// (models.SUBMISSION_STATUSES, DISTRIBUTION_TYPES, ACCOUNT_TYPES) and pinning
// these tables to it — backend/tests/test_screen_word_parity.py fails if they
// disagree in either direction. Prior art, copied deliberately:
// backend/tests/test_currency_list_parity.py.
//
// ★ IT DOES NOT READ THE SERVER'S DOORS, and saying so plainly is the point.
// ZEN-634's receipts fence also parses every write in the product code, so a
// door inventing a word fails even when both lists are updated by hand. That
// half is NOT here: an attempt to extend it to these three fields was removed
// at review round two and is ZEN-648. So if you add a word to models.py, check
// the doors yourself — nothing here will.
//
// (2) is answered by `nameOf` below, and by the check in that same test file
// that refuses ANY lookup on these screens that falls back to its own key. That
// is the half that ends the class rather than these four instances: the next
// table somebody adds cannot make this mistake without failing the build.
//
// NOT ZEN-336. That asks where the server's list should live for every kind of
// status across the 23 files that name one — a design decision, still open.
// These are three known sets read by four known screens.

// The beneficiary's own receipts screen. `null` is not in this table on
// purpose: a receipt that has never entered the submission flow has no
// submission word at all, and that screen does not list it.
export const SUBMISSION_STATUS_LABEL = {
  needs_student_review: "Needs your review",
  awaiting_owner_approval: "With owner",
  approved: "Approved",
  returned: "Returned",
  rejected_by_owner: "Rejected",
  // WRITTEN BY THE SERVER SINCE BEFORE THIS TICKET AND NAMED NOWHERE. The
  // cancel door writes this word beside `is_deleted`, and the beneficiary's
  // list hides deleted rows — so it has never actually been on screen. It is
  // named here because "unreachable today" is not a property anybody is
  // maintaining: the day that filter changes, this is the difference between
  // "Withdrawn" and `cancelled_by_student`.
  //
  // ONE WORD, NOT "WITHDRAWN BY BENEFICIARY", AND THE REASON IS WHO READS IT.
  // Withdrawing belongs to the beneficiary role and to nothing else — the
  // account owner's two actions are Return and Reject, which have their own
  // words — and a withdrawn receipt leaves the Approvals list entirely, so the
  // account owner never sees this at all. It appears on the beneficiary's own
  // screen, beside a button that says Withdraw and a confirmation that says
  // Withdrawn. Naming them in the third person there would be odd.
  // (Maryse, 2026-09-15.)
  cancelled_by_student: "Withdrawn",
};

// The 529 distributions list. The badge is only drawn for the three kinds that
// are NOT an ordinary qualified expense, but the whole set is named so the
// parity check has both halves to compare.
export const DISTRIBUTION_TYPE_LABEL = {
  qualified_education: "Qualified expense",
  student_loan_repayment: "Student loan repayment",
  roth_rollover: "Roth IRA rollover",
  // THE ODD ONE OUT OF FOUR. (ZEN-664, Maryse 2026-09-17, replacing the
  // 2026-09-15 wording "Not 529-qualified".) The other three name a THING a
  // family did — a qualified expense, a student loan repayment, a Roth IRA
  // rollover. This one named an ATTRIBUTE the other three lack, which is why
  // it read badly the moment it appeared inside a sentence. The wording below
  // names a thing too, and carries "529" so it cannot be mistaken for a
  // judgement about the expense itself.
  //
  // IT IS NOT MATCHED TO IRS VOCABULARY, and anyone who believes it is will
  // change the wrong thing next. Checked 2026-09-17: the IRS page on qualified
  // tuition programs and the IRS 529 questions-and-answers page do not use the
  // phrase "non-qualified distribution" for 529s at all — they describe a
  // distribution larger than the beneficiary's qualified education expenses,
  // with part of the earnings taxable. That phrase is plan-provider
  // vocabulary. (Not verified: Publication 970 and the Form 5329
  // instructions were not read.)
  non_qualified: "Non-qualified 529 distribution",
};

// ── HOW A BENEFICIARY LIVES, IN ONE WORDING ─────────────────────────────────
//
// THREE COPIES, AND THEY DID NOT AGREE. The same living arrangement was written
// one way where you set it (the beneficiary setup), a second way on the expense
// panel's period picker, and a THIRD way in the downloaded tax document
// (backend/expense_logic.py::SITUATION_LABELS). A family set "Off campus — with
// roommate(s)", saw "Off campus roommates" on the next screen, and read
// "Off campus · with roommate(s)" on the paperwork. Nothing was wrong with the
// numbers, and the housing cap depends on this fact, which is exactly the sort
// of disagreement that makes somebody doubt a document they are relying on.
//
// Maryse chose the tax document's wording as the one to keep (2026-09-15), so
// the paperwork is unchanged and the two screens move to match it.
// backend/tests/test_screen_word_parity.py compares this table against the
// server's, keys AND words, so a fourth wording cannot appear.
export const HOUSING_SITUATION_LABEL = {
  on_campus: "On campus",
  off_campus_alone: "Off campus · living alone",
  off_campus_roommates: "Off campus · with roommate(s)",
  off_campus_dependents: "Off campus · with dependent(s)",
  with_parents: "With parents / family",
  other: "Other",
};

// The order the choices appear in on the beneficiary setup form. An order is a
// decision about a form; the words above are a decision about the product.
export const HOUSING_SITUATION_ORDER = [
  "on_campus", "off_campus_alone", "off_campus_roommates",
  "off_campus_dependents", "with_parents", "other",
];

// The order the kinds appear as buttons on the distributions form. Same shape
// and same reason as HOUSING_SITUATION_ORDER below: an order is a decision about
// a form, the words are a decision about the product. Pinned against the table
// above by backend/tests/test_screen_word_parity.py, because the form's own copy
// of these keys was a hand-written list nobody checked — a fifth kind added on
// the server and named on the screens would have had NO BUTTON, so nobody could
// record that kind of distribution, and the build would have said nothing.
// (Found by reviewing this change, 2026-09-15.)
export const DISTRIBUTION_TYPE_ORDER = [
  "qualified_education", "student_loan_repayment", "roth_rollover", "non_qualified",
];

// The accounts screen, live cards and archived ones.
export const ACCOUNT_TYPE_LABEL = {
  checking: "Checking",
  savings: "Savings",
  credit_card: "Credit Card",
  prepaid: "Prepaid",
  other: "Other",
};

// ── WHAT A SCREEN SAYS WHEN THE WORD IS NOT IN THE TABLE ────────────────────
//
// ONE SENTENCE, NOT ONE PER SCREEN, and that is Maryse's call rather than a
// tidiness preference. Her argument: a new word means the app has to be changed
// anyway, and the check in backend/tests/test_screen_word_parity.py now STOPS
// THE BUILD if a word is added without a name — so none of these can appear in
// the ordinary course of events. A phrase nobody should ever read is not worth
// six separate decisions, and a family who does see one needs to know it is a
// fault rather than a state of their money.
export const UNRECOGNISED_WORD = "Not recognised — please report this";

// THE ONE EXCEPTION, AND IT IS A DIFFERENT THING. An account whose kind the app
// cannot name is usually an account whose kind nobody ever said — the details
// are for a family tracing their own spending, not a tax requirement. "Other"
// was the first draft and it was wrong: it claims somebody chose Other. This is
// the phrase the Payment Account dropdown on the expense panel already uses, so
// a family meets the same words twice. (Maryse, 2026-09-15.)
export const UNKNOWN_ACCOUNT_TYPE = "— Not specified —";

// THE ONE WAY TO TURN A STORED WORD INTO A PROPER NAME.
//
// It is a thin function and that is the point: `TABLE[word] || word` is one
// character away from correct and reads as deliberate, so it survived four
// review passes on four screens. This spelling cannot express the mistake —
// there is nowhere to put the raw word — and the fallback is an argument, so
// leaving it out is a missing argument rather than a silent default.
export const nameOf = (table, word, whenUnknown) =>
  (word != null && Object.prototype.hasOwnProperty.call(table, word))
    ? table[word]
    : whenUnknown;

export const CLOSED_REASON_LABELS = {
  settled_529: "Settled via 529",
  settled_privately: "Settled privately",
  not_reimbursed_aotc: "Not reimbursed — AOTC preservation",
  not_reimbursed_ineligible: "Not reimbursed — not 529-eligible",
};

// THE ONE ANSWER to the line under a closed expense's status. An expense the
// 529 settled with part of it reserved for the tax credit says both
// (Maryse, 2 Nov 2026, ZEN-334). Reserved means what the 529 side reads:
// reserve switched on with an amount above zero.
export const SETTLED_529_AND_AOTC = "Settled via 529 and AOTC reserve";
export const closedReasonLabel = (e) => {
  if (!e || e.status !== "closed") return null;
  if (e.closed_reason === "settled_529" && e.aotc_reserved
      && Number(e.aotc_reserved_amount) > 0.005) return SETTLED_529_AND_AOTC;
  return nameOf(CLOSED_REASON_LABELS, e.closed_reason, null);
};

export const EXPENSE_TYPE_LABEL = {
  eligible_owner_paid: "Eligible · Owner paid",
  eligible_student_paid: "Eligible · Student paid",
  "529_direct": "Eligible · 529 direct",
  non_eligible_reimbursed: "Not eligible · Owner reimburses",
  non_eligible_own_money: "Not eligible · Own money",
};

export const taxYears = () => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2, y - 3];
};

// For withdrawals: never allow a future tax year (you can't withdraw for a year that hasn't started)
export const taxYearsNonFuture = () => {
  const y = new Date().getFullYear();
  return [y, y - 1, y - 2, y - 3, y - 4];
};

// ---- Date display format preference (stored locally) ----
const _MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const _MONTHS_LC = _MONTHS.map(m => m.toLowerCase());
const _MONTHS_FULL = ["january","february","march","april","may","june","july","august","september","october","november","december"];

/**
 * Lenient date parser — accepts many typed formats and returns ISO yyyy-mm-dd or null.
 * Handles: 30/Jul/2026, 30/07/2026, 30 Jul 2026, 2026-07-30, Jul 30 2026, 07/30/2026,
 * and the user's active display format. Validates the resulting date is real.
 */
// Local-calendar today (or any Date) as YYYY-MM-DD. Never use
// new Date().toISOString().slice(0, 10) for a DATE the user sees: toISOString
// is UTC, which rolls to tomorrow at 8pm Eastern — so every evening the expense
// form pre-filled a "future" date and the transaction-date validator refused
// its own default. The validator side (dateValidation.todayIso) was already
// local; this is its pre-fill counterpart. Timestamps (created_at etc.) are a
// different thing and stay full UTC ISO on purpose.
export function localISO(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function parseDate(input) {
  if (!input) return null;
  const s = String(input).trim();
  if (!s) return null;

  // Try ISO first: yyyy-mm-dd
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) return _validate(+iso[1], +iso[2], +iso[3]);

  // Split on / - . or space
  const parts = s.split(/[\s/\-.,]+/).filter(Boolean);
  if (parts.length < 3) return null;

  // Try to find a month NAME in parts. The test requires a letter: _parseMonth
  // also accepts a bare 1-12, so without it "09/Aug/2026" matches the day "09"
  // as the month, leaves ["Aug","2026"] with only one number, and the date is
  // rejected — every day from the 1st to the 12th failed in dd/Mmm/yyyy, the
  // app's own International rendering. All-numeric input falls through to the
  // format-driven branch below, which is where it belongs.
  let y, m, d;
  const monthIdx = parts.findIndex(p => /[A-Za-z]/.test(p) && _parseMonth(p) !== null);

  if (monthIdx !== -1) {
    m = _parseMonth(parts[monthIdx]);
    const nums = parts.filter((_, i) => i !== monthIdx).map(Number).filter(n => !isNaN(n) && n > 0);
    if (nums.length < 2) return null;
    // Larger number is year if > 31, otherwise last number is year
    if (nums[0] > 31) { y = nums[0]; d = nums[1]; }
    else if (nums[1] > 31) { d = nums[0]; y = nums[1]; }
    else { d = nums[0]; y = nums[1]; } // ambiguous — treat first num as day
  } else {
    // All numeric: determine order from active format
    const n = parts.map(Number);
    if (n.some(isNaN)) return null;
    const fmt = getDateFormat();
    if (fmt === "us") { m = n[0]; d = n[1]; y = n[2]; }             // mm/dd/yyyy
    else if (fmt === "iso") { y = n[0]; m = n[1]; d = n[2]; }       // yyyy-mm-dd
    else { d = n[0]; m = n[1]; y = n[2]; }                           // intl, eu, long → dd/mm/yyyy
  }

  // Expand 2-digit year
  if (y != null && y < 100) y += y < 50 ? 2000 : 1900;
  return _validate(y, m, d);
}

function _parseMonth(s) {
  const lc = String(s).toLowerCase();
  const n = parseInt(lc, 10);
  if (!isNaN(n) && n >= 1 && n <= 12) return n;
  let idx = _MONTHS_LC.indexOf(lc);
  if (idx !== -1) return idx + 1;
  idx = _MONTHS_FULL.indexOf(lc);
  if (idx !== -1) return idx + 1;
  // partial match (3+ chars)
  if (lc.length >= 3) {
    idx = _MONTHS_LC.findIndex(m => m.startsWith(lc));
    if (idx !== -1) return idx + 1;
    idx = _MONTHS_FULL.findIndex(m => m.startsWith(lc));
    if (idx !== -1) return idx + 1;
  }
  return null;
}

// Is this a date that exists? No year window, deliberately: _validate below
// rejects anything outside 1900-2200 because it guards TYPED INPUT, where a
// four-digit typo is the likely explanation. This one guards DISPLAY of a value
// already stored, where a real 1899 date must still render. It matches what
// Python's strptime accepts, which is the rule the tax documents use.
function _isRealDate(y, m, d) {
  // Year zero does not exist on this calendar, and "0000-01-01" is one of the two
  // strings the audit packet names as the right shape and not a date. Python's
  // date type starts at year 1 and JavaScript's does not, so without this line the
  // screen and the tax document disagree about that exact value.
  if (y < 1) return false;
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCFullYear(y);  // years 0-99 would otherwise be read as 19xx
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function _validate(y, m, d) {
  if (y == null || m == null || d == null) return null;
  if (y < 1900 || y > 2200 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
export const DATE_FORMATS = [
  { value: "us", label: "US — 07/01/2025", hint: "mm/dd/yyyy" },
  { value: "intl", label: "International — 01/Jul/2025", hint: "dd/mmm/yyyy" },
  { value: "iso", label: "ISO — 2025-07-01", hint: "yyyy-mm-dd" },
  { value: "eu", label: "European — 01/07/2025", hint: "dd/mm/yyyy" },
  { value: "long", label: "Long — Jul 1, 2025", hint: "mmm d, yyyy" },
];
export const getDateFormat = () => localStorage.getItem("dateFormat") || "us";
export const setDateFormatPref = (v) => localStorage.setItem("dateFormat", v);
export const getDateFormatHint = () => (DATE_FORMATS.find(f => f.value === getDateFormat()) || DATE_FORMATS[0]).hint;

// Format an ISO-ish date string ("YYYY-MM-DD" or full ISO) per the user's preference.
// Parses the calendar date directly (no timezone shift). Returns the input unchanged
// if it isn't a parseable date.
//
// A REAL CALENDAR DATE, NOT THE RIGHT SHAPE. (ZEN-92.) The check here used to be
// the regex alone, so "2025-13-40" matched and was rearranged into "40/13/2025" —
// nonsense presented as a date. That is the same mistake the audit packet already
// removed from its own year check: 2025-13-40 and 0000-01-01 are the right SHAPE
// and are not dates, and parsing is the only test that tells a date from a string
// that resembles one. Text that is not a date is shown exactly as it is stored, so
// a person can see there is something to fix.
//
// This matters more than it used to, because since ZEN-92 the downloaded tax
// documents run the same rule (backend/report_dates.py) and the two are pinned to
// one fixture table — date-format-fixtures.json. A screen that renders an
// impossible date and a packet that does not would be the divergence this change
// exists to end.
export const fmtDate = (iso, fmt) => {
  if (!iso) return "";
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(iso);
  if (!_isRealDate(+m[1], +m[2], +m[3])) return String(iso);
  const [, y, mo, d] = m;
  const mon = _MONTHS[parseInt(mo, 10) - 1];
  switch (fmt || getDateFormat()) {
    case "intl": return `${d}/${mon}/${y}`;
    case "iso": return `${y}-${mo}-${d}`;
    case "eu": return `${d}/${mo}/${y}`;
    case "long": return `${mon} ${parseInt(d, 10)}, ${y}`;
    case "us":
    default: return `${mo}/${d}/${y}`;
  }
};
